/**
 * Origin resolution for web and native (Capacitor) builds.
 *
 * On the web every function here collapses to `window.location.origin` and nothing
 * changes. Inside a Capacitor WebView the runtime origin becomes `capacitor://localhost`
 * (iOS) or `https://localhost` (Android), which silently breaks anything that assumed
 * the origin was the brand's public domain.
 *
 * Three notions were previously conflated behind a single `window.location.origin`:
 *
 *   - **runtime origin** — where the WebView actually is. Correct for comparing against
 *     other runtime URLs. Must never be sent anywhere, because in the app it is a
 *     meaningless local value.
 *   - **public origin** — the brand's real domain (`NUXT_PUBLIC_SITE_DOMAIN`, baked in at
 *     build time). Correct for anything leaving the client: API headers, analytics
 *     payloads, form submissions.
 *   - **auth redirect** — `origin + '/lms'` on the web, a custom scheme on native.
 *
 * Sending the runtime origin to the backend does not error — it corrupts data. The API
 * writes `page_views.page_location` as `{X-LMS-Requester}{page_path}`, so an app build
 * would quietly record rows like `capacitor://localhost/lms/courses`.
 *
 * See docs/capacitor-mobile-app-feasibility.md §5 items 1, 4, 6, 10 and 11.
 */

/** Bundle ID / Capacitor `appId`. Also embedded in the Auth0 native callback URL and the
 *  Universal Links / App Links association files — changing it means re-registering
 *  the Auth0 application and regenerating those files. See docs/auth0-settings.md. */
export const NATIVE_BUNDLE_ID = 'org.ipolo.lms';

/**
 * True when running inside a Capacitor native WebView.
 *
 * Detected via the `window.Capacitor` global that the native bridge injects, rather than
 * by importing `@capacitor/core`. That keeps this module dependency-free, so it can ship
 * and be tested on the web before Capacitor is ever installed — and so web builds never
 * pull the package in.
 */
export function isNativeApp(): boolean {
   if (typeof window === 'undefined') return false;

   const bridge = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
   if (typeof bridge?.isNativePlatform !== 'function') return false;

   return bridge.isNativePlatform();
}

/**
 * True when the WebView is serving the app's **own bundled assets**, so the origin is
 * Capacitor's synthetic one — `capacitor://localhost` on iOS, `https://localhost` on
 * Android.
 *
 * Distinct from `isNativeApp()`, and the distinction matters. Capacitor injects its
 * bridge into the WebView even when `server.url` points at a remote site (the Phase 0
 * diagnostic), so `isNativeApp()` is true there too — but the origin is then an ordinary
 * http(s) origin that Auth0 can redirect back to. Only the bundled origin cannot receive
 * a URL redirect and needs the custom scheme.
 *
 * See docs/capacitor-mobile-app-feasibility.md §10 for the Phase 0 / Phase 1 split.
 */
export function isBundledNativeOrigin(): boolean {
   const origin = resolveRuntimeOrigin();

   return origin.startsWith('capacitor://') || origin === 'https://localhost';
}

/**
 * Where the WebView is actually running. Empty string during SSR/prerender, and also
 * when `location.origin` is absent — some environments expose a partial location object,
 * and returning '' keeps callers from interpolating the string "undefined" into a URL.
 */
export function resolveRuntimeOrigin(): string {
   if (typeof window === 'undefined') return '';

   return window.location?.origin || '';
}

/**
 * The brand's public web origin — always a real, resolvable domain.
 *
 * Use for every value that leaves the client. Falls back to the runtime origin when no
 * site domain is configured (local dev), which is the pre-existing behaviour.
 */
export function resolvePublicOrigin(siteDomain?: string | null): string {
   if (!siteDomain) return resolveRuntimeOrigin();

   return siteDomain.replace(/\/+$/, '');
}

/**
 * The `X-LMS-Requester` header, built from a canonical origin.
 *
 * Every request that leaves the client carries this, and it is not just an identifier — the
 * API validates it against an allow-list and **builds Stripe's `success_url`, `cancel_url`
 * and `return_url` from it**, so a malformed value produces malformed redirects after a
 * payment. It is also what `page_views.page_location` is prefixed with.
 *
 * A single function rather than the object literal that used to be repeated at nine call
 * sites, only one of which normalised the value. A trailing slash in any `.env` therefore
 * leaked into the other eight, and one build could send two different requester strings for
 * the same site — `.env.staging` has exactly that shape today.
 *
 * **Hygiene, not a bug fix.** The API normalises in every consumer — allow-list validation,
 * the requester middleware, the checkout and cancel URL builders, the billing portal return
 * URL, and analytics `page_location` — and its maintainer verified that `host`, `host/` and
 * `host///` produce byte-identical output. A trailing slash is therefore *accepted* rather
 * than rejected, and produces no malformed URL. What this removes is the discrepancy, and
 * the chance that a future `.env` hands a trailing slash to some consumer that does not
 * normalise.
 *
 * Use this instead of writing the header by hand, so the ninth call site cannot get it wrong.
 */
export function requesterHeaders(siteDomain?: string | null): { 'X-LMS-Requester': string } {
   return { 'X-LMS-Requester': resolvePublicOrigin(siteDomain) };
}

/**
 * An absolute URL on the brand's public site, for analytics and form payloads that
 * previously used `window.location.href`.
 */
export function resolvePublicUrl(siteDomain: string | null | undefined, path: string): string {
   const origin = resolvePublicOrigin(siteDomain);
   const suffix = path.startsWith('/') ? path : `/${path}`;

   return `${origin}${suffix}`;
}

/** The Auth0 callback URL for the native app, per Auth0's Capacitor convention. */
export function nativeAuthCallback(auth0Domain: string): string {
   return `${NATIVE_BUNDLE_ID}://${auth0Domain}/capacitor/${NATIVE_BUNDLE_ID}/callback`;
}

/**
 * Auth0 `redirect_uri`.
 *
 * Web behaviour is unchanged: the runtime origin plus `/lms`. Deliberately the *runtime*
 * origin and not the public one, so localhost and CloudFront preview URLs keep working.
 * On the bundled native origin the browser-based redirect cannot come back to a URL, so
 * it must be the custom scheme registered in Auth0.
 *
 * Gated on the *origin*, not merely on running inside Capacitor. Under the Phase 0
 * diagnostic the bridge is present but the page is served from a real domain, so the
 * ordinary web redirect is both valid and the only one that works — the custom scheme has
 * no `CFBundleURLTypes` entry and no `appUrlOpen` handler until Phase 1. Gating on
 * `isNativeApp()` alone sent Auth0 the custom scheme from a Phase 0 build and failed with
 * `unauthorized_client: Callback URL mismatch`.
 */
export function resolveAuthRedirectUri(auth0Domain?: string | null): string {
   if (isNativeApp() && isBundledNativeOrigin() && auth0Domain) return nativeAuthCallback(auth0Domain);

   return `${resolveRuntimeOrigin()}/lms`;
}

/** Auth0 logout `returnTo`. Same reasoning as `resolveAuthRedirectUri`. */
export function resolveAuthLogoutReturnTo(auth0Domain?: string | null): string {
   if (isNativeApp() && isBundledNativeOrigin() && auth0Domain) return nativeAuthCallback(auth0Domain);

   return resolveRuntimeOrigin();
}
